import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import api from "../../api/api";
import httpAuth from "../../client/httpAuth";
import httpClient from "../../client/httpClient";

const AUTH_ENDPOINTS = {
    LOGIN: "/api/auth/login",
    SELLER_REGISTER: "/api/auth/seller-register",
    PROFILE_IMAGE_UPLOAD: "/profile-image-upload",
    PROFILE_INFO_ADD: "/profile-info-add"
};

const STORAGE_KEYS = {
    ACCESS_TOKEN: "access_token",
    REFRESH_TOKEN: "refresh_token",
    USER_INFO: "user_info",
    SELLER: "seller",
    ROLE: "role"
};

const SELLER_STATUS = {
    APPROVE: "APPROVE",
    PENDING: "PENDING",
    DECLINE: "DECLINE"
};

const ERROR_MESSAGES = {
    PENDING_APPROVAL:
        "Your seller account is pending approval from NEON Mall. For support, please contact our Support Team.",
    INVALID_CREDENTIALS: "Email or Password is incorrect",
    STORAGE_FAILED: "Failed to save login data. Please check your browser settings.",
    ACCOUNT_DEACTIVATED: "Account is deactivated",
    NO_PERMISSION: "Your account does not have permission to access this application",
    SELLER_NOT_FOUND: "Seller not found",
    INVALID_STATUS: "This seller account is not fully activated. Please contact support.",
    VERIFICATION_FAILED: "Failed to verify seller account status",
    INVALID_USER_INFO: "Invalid user information received"
};

const authenticate = async (credentials) => {
    const response = await httpAuth.post(AUTH_ENDPOINTS.LOGIN, credentials, {
        headers: { "Content-Type": "application/json" }
    });

    const { token, userProfileDTO } = response.data;
    if (!token || !userProfileDTO) {
        throw new Error("Invalid response from server");
    }

    return { token, userProfileDTO };
};

const storeCredentials = (token, userProfileDTO) => {
    try {
        httpClient.defaults.headers.common.Authorization = `Bearer ${token}`;
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
        localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(userProfileDTO));
        localStorage.setItem(STORAGE_KEYS.ROLE, userProfileDTO.role.name);

        const savedToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        if (!savedToken) {
            throw new Error(ERROR_MESSAGES.STORAGE_FAILED);
        }
    } catch (error) {
        console.error("Storage error:", error);
        throw new Error(ERROR_MESSAGES.STORAGE_FAILED);
    }
};

const clearCredentials = () => {
    localStorage.clear();
    delete httpClient.defaults.headers.common.Authorization;
};

const validateSellerStatus = async (userProfileDTO) => {
    const response = await httpClient.get(
        `/v1/api/seller/seller_get-by-profile-id/${userProfileDTO.id}`
    );

    if (!response || response.status !== 200) {
        throw new Error(ERROR_MESSAGES.VERIFICATION_FAILED);
    }

    return response.data;
};

const handleSellerApproval = (sellerData, navigate) => {
    const { status } = sellerData;
    const role = sellerData?.userProfileDTO?.role?.name || null;

    if (!role) {
        throw new Error(ERROR_MESSAGES.INVALID_USER_INFO);
    }

    const redirectMap = {
        ROLE_SELLER: "/seller/dashboard",
        ROLE_AFFILIATE_AGENT: "/affiliate/orders",
        ROLE_NEON_WAREHOUSE_STAFF: "/staff/affiliate-orders"
    };

    switch (status) {
        case SELLER_STATUS.APPROVE:
            if (!sellerData) {
                throw new Error(ERROR_MESSAGES.SELLER_NOT_FOUND);
            }
            toast.success(
                `Login Success as ${role
                    .replace(/^ROLE_/, "")
                    .toLowerCase()
                    .replace(/_/g, " ")}`
            );
            localStorage.setItem(STORAGE_KEYS.SELLER, JSON.stringify(sellerData));
            setTimeout(() => navigate(redirectMap[role]), 300);
            return sellerData;

        case SELLER_STATUS.PENDING: {
            clearCredentials();
            throw new Error("pending_activation");
        }

        case SELLER_STATUS.DECLINE:
            clearCredentials();
            throw new Error(ERROR_MESSAGES.ACCOUNT_DEACTIVATED);

        default:
            clearCredentials();
            throw new Error(ERROR_MESSAGES.INVALID_STATUS);
    }
};

const handleRoleBasedAccess = async (token, userProfileDTO, navigate) => {
    if (!userProfileDTO?.role) {
        throw new Error(ERROR_MESSAGES.INVALID_USER_INFO);
    }

    const { role, isActive } = userProfileDTO;

    switch (role.name) {
        case "ROLE_ADMIN":
            toast.success("Login Success as Admin");
            setTimeout(() => navigate("/admin/dashboard"), 300);
            return { token, roleName: "ROLE_ADMIN", userProfileDTO };

        case "ROLE_SELLER": {
            if (!isActive) {
                clearCredentials();
                throw new Error(ERROR_MESSAGES.ACCOUNT_DEACTIVATED);
            }

            const sellerData = await validateSellerStatus(userProfileDTO);
            const seller = handleSellerApproval(sellerData, navigate);

            return {
                token,
                roleName: "ROLE_SELLER",
                userProfileDTO,
                seller
            };
        }

        case "ROLE_AFFILIATE_AGENT": {
            if (!isActive) {
                clearCredentials();
                throw new Error(ERROR_MESSAGES.ACCOUNT_DEACTIVATED);
            }

            const sellerData = await validateSellerStatus(userProfileDTO);
            const seller = handleSellerApproval(sellerData, navigate);

            return {
                token,
                roleName: "ROLE_AFFILIATE_AGENT",
                userProfileDTO,
                seller
            };
        }

        case "ROLE_NEON_WAREHOUSE_STAFF": {
            if (!isActive) {
                clearCredentials();
                throw new Error(ERROR_MESSAGES.ACCOUNT_DEACTIVATED);
            }

            const sellerData = await validateSellerStatus(userProfileDTO);
            const seller = handleSellerApproval(sellerData, navigate);

            return {
                token,
                roleName: "ROLE_NEON_WAREHOUSE_STAFF",
                userProfileDTO,
                seller
            };
        }

        default:
            throw new Error(ERROR_MESSAGES.NO_PERMISSION);
    }
};

export const admin_login = createAsyncThunk(
    "auth/admin_login",
    async ({ navigate, info }, { rejectWithValue, fulfillWithValue }) => {
        try {
            const data = await httpAuth.post(AUTH_ENDPOINTS.LOGIN, info, {
                headers: { "Content-Type": "application/json" }
            });

            httpClient.defaults.headers.common.Authorization = `Bearer ${data.data.token}`;
            localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.data.token);
            localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.data.refresh_token);

            toast.success("Login Success");
            navigate("/");
            return fulfillWithValue(data);
        } catch (error) {
            return rejectWithValue(error.response?.data || "Login failed");
        }
    }
);

export const seller_login = createAsyncThunk(
    "auth/seller_login",
    async ({ navigate, info }, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { token, userProfileDTO } = await authenticate(info);
            storeCredentials(token, userProfileDTO);
            const result = await handleRoleBasedAccess(token, userProfileDTO, navigate);

            return fulfillWithValue(result);
        } catch (error) {
            const errorMessage = error.message || "Login failed. Please try again.";

            if (error?.message === "pending_activation") {
                toast.error(ERROR_MESSAGES.PENDING_APPROVAL);
            } else if (
                error.response?.status === 401 ||
                error.response?.status === 400 ||
                error.response?.status === 500
            ) {
                toast.error(ERROR_MESSAGES.INVALID_CREDENTIALS);
            } else {
                toast.error(errorMessage);
            }

            return rejectWithValue(error.data || errorMessage);
        }
    }
);

export const logout = createAsyncThunk(
    "auth/logout",
    async ({ navigate }, { rejectWithValue, fulfillWithValue }) => {
        try {
            localStorage.clear();
            delete httpClient.defaults.headers.common.Authorization;
            navigate("/login");
            return fulfillWithValue();
        } catch (error) {
            return rejectWithValue(error.response?.data || "Logout failed");
        }
    }
);

export const profile_image_upload = createAsyncThunk(
    "auth/profile_image_upload",
    async (image, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.post(AUTH_ENDPOINTS.PROFILE_IMAGE_UPLOAD, image, {
                withCredentials: true
            });
            return fulfillWithValue(data);
        } catch (error) {
            return rejectWithValue(error.response?.data || "Upload failed");
        }
    }
);

export const seller_register = createAsyncThunk(
    "auth/seller_register",
    async (info, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.post(AUTH_ENDPOINTS.SELLER_REGISTER, info, {
                withCredentials: true
            });
            localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.token);
            return fulfillWithValue(data);
        } catch (error) {
            return rejectWithValue(error.response?.data || "Registration failed");
        }
    }
);

export const profile_info_add = createAsyncThunk(
    "auth/profile_info_add",
    async (info, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.post(AUTH_ENDPOINTS.PROFILE_INFO_ADD, info, {
                withCredentials: true
            });
            return fulfillWithValue(data);
        } catch (error) {
            return rejectWithValue(error.response?.data || "Profile update failed");
        }
    }
);

const getInitialState = () => ({
    successMessage: "",
    errorMessage: "",
    loader: false,
    userInfo: JSON.parse(localStorage.getItem(STORAGE_KEYS.USER_INFO) || "null"),
    sellerInfo: JSON.parse(localStorage.getItem(STORAGE_KEYS.SELLER) || "null"),
    sellerId: JSON.parse(localStorage.getItem(STORAGE_KEYS.SELLER) || "{}")?.id || null,
    role: localStorage.getItem(STORAGE_KEYS.ROLE) || "",
    token: localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
    status: ""
});

const authSlice = createSlice({
    name: "auth",
    initialState: getInitialState(),
    reducers: {
        messageClear: (state) => {
            state.errorMessage = "";
            state.successMessage = "";
        },
        updateUser: (state, action) => {
            state.userInfo = action.payload;
            localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(action.payload));
        },
        clearAuth: (state) => {
            Object.assign(state, {
                token: null,
                role: "",
                userInfo: null,
                sellerInfo: null,
                sellerId: null,
                successMessage: "",
                errorMessage: "",
                loader: false,
                status: ""
            });
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(admin_login.pending, (state) => {
                state.loader = true;
                state.errorMessage = "";
            })
            .addCase(admin_login.fulfilled, (state, { payload }) => {
                state.loader = false;
                state.token = payload.data.token;
                state.successMessage = "Admin login successful";
            })
            .addCase(admin_login.rejected, (state, { payload }) => {
                state.loader = false;
                state.errorMessage = payload || "Admin login failed";
            })
            .addCase(seller_login.pending, (state) => {
                state.loader = true;
                state.errorMessage = "";
            })
            .addCase(seller_login.fulfilled, (state, { payload }) => {
                state.loader = false;
                state.token = payload.token;
                state.role = payload.roleName;
                state.userInfo = payload.userProfileDTO;
                state.sellerInfo = payload.seller || null;
                state.sellerId = payload.seller?.id || null;
                state.successMessage = "Login successful";
            })
            .addCase(seller_login.rejected, (state, { payload }) => {
                state.loader = false;
                state.errorMessage = payload || "Login failed";
            })
            .addCase(logout.fulfilled, (state) => {
                Object.assign(state, {
                    token: null,
                    role: "",
                    userInfo: null,
                    sellerInfo: null,
                    sellerId: null,
                    successMessage: "Logged out successfully"
                });
            })
            .addCase(profile_image_upload.pending, (state) => {
                state.loader = true;
            })
            .addCase(profile_image_upload.fulfilled, (state, { payload }) => {
                state.loader = false;
                state.successMessage = "Profile image uploaded successfully";
            })
            .addCase(profile_image_upload.rejected, (state, { payload }) => {
                state.loader = false;
                state.errorMessage = payload || "Profile image upload failed";
            })
            .addCase(seller_register.pending, (state) => {
                state.loader = true;
            })
            .addCase(seller_register.fulfilled, (state, { payload }) => {
                state.loader = false;
                state.token = payload.token;
                state.successMessage = "Registration successful";
            })
            .addCase(seller_register.rejected, (state, { payload }) => {
                state.loader = false;
                state.errorMessage = payload || "Registration failed";
            })
            .addCase(profile_info_add.pending, (state) => {
                state.loader = true;
            })
            .addCase(profile_info_add.fulfilled, (state, { payload }) => {
                state.loader = false;
                state.successMessage = "Profile updated successfully";
            })
            .addCase(profile_info_add.rejected, (state, { payload }) => {
                state.loader = false;
                state.errorMessage = payload || "Profile update failed";
            });
    }
});

export const { messageClear, updateUser, clearAuth } = authSlice.actions;
export default authSlice.reducer;
